/**
 * Custom hook to detect user location based on IP geolocation
 * and fallback to browser-based geolocation if necessary.
 *
 * This hook provides the detected region, country flag, phone code,
 * and coordinates, allowing for centralized management of user location.
 *
 * @module useUserLocation
 * @function useUserLocation
 * @returns {Object} - An object containing the user's region, country flag, phone code, and latLng.
 * @example
 * const { countryFlag, phoneCode, userRegion, latLng } = useUserLocation();
 *
 * @created 2024/10/28
 * @modified 2024/10/28
 * @author Vladimir Shishlyannikov
 * @copyright 2024-present iWoo Ltd.
 * All rights reserved.
 */
import { useEffect, useState, useCallback } from "react";
import * as Sentry from "@sentry/react";

// Hooks
import useCountryName from "../useCountryName/useCountryName";
import { VALID_US_CITIES, reverseMapping, CITY_MAPPING } from "../../constants";

function getStoredRegion() {
  const storedData = localStorage.getItem("userRegion");
  try {
    if (storedData && storedData.startsWith("{")) {
      return JSON.parse(storedData);
    }
    return { region: storedData || null, city: null };
  } catch (e) {
    return { region: storedData || null, city: null };
  }
}

function getInitialRegion() {
  const hostBasedRegion =
    window.location.host === "us.trubeapp.com" ||
    window.location.pathname.startsWith("/us")
      ? "US"
      : "GB";

  const storedData = getStoredRegion();
  return {
    region: storedData.region || hostBasedRegion,
    city: storedData.city || null,
  };
}

function useUserLocation(user) {
  const [countryFlag, setCountryFlag] = useState(null);
  const [phoneCode, setPhoneCode] = useState(null);
  const [userRegion, setUserRegion] = useState(getInitialRegion().region);
  const [userCity, setUserCity] = useState(getInitialRegion().city);
  const [latLng, setLatLng] = useState(null);

  const [, countryShortName, lookupAddress] = useCountryName(latLng);

  useEffect(() => {
    if (user) {
      setUserRegion(user.region);
      setUserCity(user.city); //);reverseMapping[user.city] || null);
    }
  }, [user]);

  useEffect(() => {
    // If region is already in local storage, use it directly
    if (
      userRegion === "US" &&
      userCity /* && VALID_US_CITIES.includes(userCity)*/
    ) {
      return;
    }

    const fetchLocationData = async () => {
      try {
        const response = await fetch(
          `https://api.ipgeolocation.io/ipgeo?apiKey=${process.env.REACT_APP_IPGEOLOCATION_API_KEY}`
        );
        const data = await response.json();
        const countryCode = data.country_code2;

        let detectedRegion;
        let detectedCity = null;
        if (countryCode === "US") {
          setCountryFlag("🇺🇸");
          setPhoneCode("+1");
          detectedRegion = "US";
          // Проверяем, что город из допустимого списка, иначе дефолтим до LA
          // FIXME лучше пофиксить, т.к. города с бека могут прийти другие
          detectedCity = VALID_US_CITIES.includes(data.city)
            ? data.city
            : "LOS_ANGELES";
        } else {
          setCountryFlag("🇬🇧");
          setPhoneCode("+44");
          detectedRegion = "GB";
          detectedCity = "LONDON";
        }

        setUserRegion(detectedRegion);
        setUserCity(
          CITY_MAPPING[detectedCity] || detectedCity /*detectedCity*/
        ); // null если город невалидный
      } catch (error) {
        Sentry.captureMessage(
          `IP geolocation failed: ${JSON.stringify(error)}`
        );
        // Fallback to browser-based geolocation
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const latLngValue = `${position.coords.latitude},${position.coords.longitude}`;
              setLatLng(latLngValue);
              lookupAddress(latLngValue); // Perform address lookup if needed
            },
            (err) => {
              console.warn(`Geolocation error (${err.code}): ${err.message}`);
              // Fallback coordinates if user denies location access
              setLatLng("34.098907,-118.327759");
            }
          );
        }
      }
    };

    if (!user) {
      fetchLocationData();
    }
  }, [userRegion, userCity, lookupAddress]);

  return {
    countryFlag,
    phoneCode,
    userLocation: userRegion,
    userCity,
    latLng,
  };
}

export default useUserLocation;
